import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    MessageMeasuresConstantType,
    MessageDynamicStyleType,
    MessageMeasuresKeysType,
    MessageMeasures,
    MessageAlign,
    MessageSize
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    webStyle
} from "../../utils";

export const MESSAGE_AVATAR_SIZES: Record<MessageSize, keyof NCoreUIKit.AvatarSizes> = {
    medium: "xSmall",
    small: "xxSmall",
    large: "small"
};

export const MESSAGE_ROW_SIZES: Record<MessageSize, keyof NCoreUIKit.ControlHeights> = {
    medium: "medium",
    large: "large",
    small: "small"
};

export const MESSAGE_PLAIN_AVATAR_SIZE: keyof NCoreUIKit.AvatarSizes = "xxSmall";

export const MESSAGE_FOOTER_TYPOGRAPHY: keyof NCoreUIKit.Typography = "bodySmallSize";

export const MESSAGE_DIRECTIONS: Record<MessageAlign, ViewStyle["flexDirection"]> = {
    end: "row-reverse",
    start: "row"
};

export const MESSAGE_CROSS_ALIGNMENTS: Record<MessageAlign, ViewStyle["alignItems"]> = {
    start: "flex-start",
    end: "flex-end"
};

export const getMessageMeasuresKeys = ({
    size
}: {
    size: MessageSize;
}): MessageMeasuresKeysType => {
    return {
        avatarSize: MESSAGE_AVATAR_SIZES[size]
    };
};

export const getMessageMeasures = ({
    controlHeights,
    isHasReactions,
    shapeConfigs,
    avatarSizes,
    isHasFooter,
    typography,
    spaces,
    size
}: MessageMeasuresConstantType): MessageMeasures => {
    const {
        avatarSize: avatarSizeKey
    } = getMessageMeasuresKeys({
        size
    });

    const avatarSize = avatarSizes[avatarSizeKey];

    const footerHeight = typography[MESSAGE_FOOTER_TYPOGRAPHY].lineHeight;

    const reactionsOffset = isHasReactions ? (controlHeights.small - shapeConfigs.chipHeightOffset) / 2 : 0;

    const footerOffset = isHasFooter ? footerHeight + spaces.spacingXs : 0;

    return {
        avatarOffset: (controlHeights[MESSAGE_ROW_SIZES[size]] - avatarSize) / 2 + reactionsOffset + footerOffset,
        reactionsOffset,
        footerHeight,
        avatarSize
    };
};

const stylesheet = StyleSheet.create({
    container: {
        alignItems: "flex-end",
        width: "100%",
        minWidth: 0,
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    content: {
        flexDirection: "column",
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 0
    },
    rows: {
        flexDirection: "column",
        width: "100%",
        minWidth: 0
    },
    avatar: {
        alignSelf: "flex-end",
        flexShrink: 0
    }
});

export const useStyles = ({
    isFocusVisible,
    isAvatarSlot,
    shapeConfigs,
    isSystem,
    measures,
    radiuses,
    borders,
    colors,
    spaces,
    align
}: MessageDynamicStyleType) => {
    const styles = {
        content: {
            alignItems: isSystem ? "center" : MESSAGE_CROSS_ALIGNMENTS[align],
            ...getCornerStyle({
                radius: radiuses.surface,
                shapeConfigs
            }),
            gap: spaces.spacingXs,
            ...getFocusRingStyle({
                isFocusVisible,
                borders,
                colors
            })
        } as Mutable<ViewStyle>,
        rows: {
            alignItems: isSystem ? "center" : MESSAGE_CROSS_ALIGNMENTS[align],
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        container: {
            flexDirection: MESSAGE_DIRECTIONS[align],
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        avatar: {
            marginBottom: measures.avatarOffset,
            width: measures.avatarSize
        } as Mutable<ViewStyle>
    };

    if(!isAvatarSlot) {
        delete styles.avatar.width;
    }

    return styles;
};
export default stylesheet;
